import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BaseParentComponent } from '@components/_base/base-parent/base-parent.component';
import { RegisterOfLegalPersonsModalComponent } from '@components/register-of-legal-persons-modal/register-of-legal-persons-modal.component';
import { modalMinWidth } from '@env/config';
import { displayError } from '@app/_utils/error-util';
import { formatDateTimeForDto, parseDtoDateTime } from '@app/_utils/date-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { AuthenticationService } from '@app/login/_services/authentication.service';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { NomenclatureService } from '@services/nomenclature.service';
import { PromotionService, CustomerPromocodeMobileDTO } from '@services/promotion.service';
import { ActiveDiscountCampaignDTO, PromotionalCampaign } from '@services/promotional-campaign.service';
import { PaymentType } from '@models/payment-type.model';
import { RvsCategory } from '@models/rvs-category.model';
import { SubjectVersion } from '@models/subject-version.model';
import { TechPointService } from '@app/tech-points/_models/tech-point-service.model';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { TechnicalInspectionService } from '@app/technical-inspections/_services/technical-inspection.service';

import { IcashCatalogItem } from '../../_models/icash-catalog-item.model';
import { Sale } from '../../_models/sale.model';
import { SaleService as SaleServiceModel } from '../../_models/sale-service.model';
import { SaleItem } from '../../_models/sale-item.model';
import { SaleService } from '../../_services/sale.service';

/** A line of the "сметка за плащане" grid — either an additional service or an iCash article. */
interface BillLine {
  kind: 'SERVICE' | 'ITEM';
  name: string;
  quantity: number;
  /** Gross unit price. */
  unitPrice: number;
  /** Discount for the whole line, in currency, editable by the operator. */
  discount: number;
  /** Set on service lines. */
  techPointServiceId?: number;
  /** Set on item lines. */
  saleItemId?: number;
  /**
   * What the operator typed into the discount box, when they have typed anything.
   *
   * Kept apart from `discount` because that field is recomputed whenever the bill changes: a
   * promo code, a campaign and a quantity change all rewrite it. Without somewhere to remember
   * the operator's own figure it would be lost the next time any of those ran.
   */
  typedDiscount?: number;
  /**
   * The active campaign discounting this line, if one does. Null on an item line - promotions
   * are configured against services - and on a line restored from a stored sale.
   */
  campaign?: AppliedCampaign;
}

/**
 * A promotional campaign in force on one bill line.
 *
 * Unlike a promo code, nothing is applied by hand: the campaign is matched to the service by
 * name as soon as it reaches the bill. `isFree` marks the second kind of campaign, where buying
 * a trigger service makes another one free rather than merely cheaper.
 */
interface AppliedCampaign {
  serviceName: string;
  techPointServiceId: number;
  /** The campaign's own wording, for the chip. */
  discountText: string;
  isFree: boolean;
}

/**
 * A promo code the operator applied, bound to the one service it discounts.
 *
 * Several codes can be in force at once — one per service, as on the technical inspection
 * screen — so they are kept as a list rather than a single applied code.
 */
interface AppliedPromocode {
  code: string;
  /** The service the code discounts; its name is how the backend identifies it. */
  serviceName: string;
  techPointServiceId: number;
  promocodeId: number;
  /** 'P' for a percentage, anything else for an absolute amount. */
  discountKind: string;
  discountValue: number;
}

@Component({
  selector: 'app-add-edit-sales',
  templateUrl: './add-edit-sales.component.html',
  styleUrls: ['./add-edit-sales.component.css']
})
export class AddEditSalesComponent extends BaseParentComponent implements OnInit, OnDestroy {

  /** The "are you sure" shown before a sale is voided. */
  @ViewChild('annulConfirm') annulConfirmDialog: TemplateRef<any>;

  // Service type 2 is ADDITIONAL_SERVICE. The backend widens anything that is not a basic
  // service or an annual subscription to additional services *and* packages, which is what the
  // "допълнителни услуги" section is meant to offer.
  private readonly ADDITIONAL_SERVICE_TYPE = 2;

  private readonly CATALOG_PAGE_SIZE = 10;
  private readonly SEARCH_DEBOUNCE_MS = 400;

  /** How long a rejection stays on screen before it clears itself. */
  private readonly MESSAGE_TIMEOUT_MS = 5000;

  /** Promotion discount kinds, as the backend names them. */
  private readonly DISCOUNT_PERCENTAGE = 'P';

  /**
   * The ways a counter sale can be settled, from PaymentTypeEnum: На каса в пункт, По банка
   * and Отложено плащане. The nomenclature also holds Карта, ePay, EasyPay and Киоск, which
   * belong to the reservation and kiosk flows and cannot be taken at the counter, so the
   * shared endpoint's list is narrowed here rather than for every other screen that reads it.
   */
  private readonly PAYMENT_TYPE_IN_TECH_POINT_PAY_DESK = 1;
  private readonly SALE_PAYMENT_TYPE_IDS = [this.PAYMENT_TYPE_IN_TECH_POINT_PAY_DESK, 5, 6];

  // Units
  userTechPointMobileAppIds: number[] = [];
  technicalPointId: number;

  /** Set when the screen was opened on an existing sale; null while creating one. */
  saleId: number | null = null;
  /** Held only between loading a sale and rebuilding its lines, then released. */
  private editedSale: Sale | null = null;
  /** The sale being edited already has an invoice, which this edit will not change. */
  hasInvoice = false;

  // Payload
  rvsCategories: RvsCategory[] = [];
  paymentTypes: PaymentType[] = [];
  additionalServices: TechPointService[] = [];
  catalogResults: IcashCatalogItem[] = [];
  billLines: BillLine[] = [];
  recipient: SubjectVersion | null = null;
  /** One per discounted service — a service already discounted will not take a second code. */
  appliedPromocodes: AppliedPromocode[] = [];
  /**
   * The campaigns running right now, as configured in промо кампании.
   *
   * Loaded once on init and then consulted locally: a campaign applies to a service by name, so
   * no further call is needed as lines come and go.
   */
  activeDiscountCampaigns: ActiveDiscountCampaignDTO[] = [];
  /**
   * The services that came back from a stored sale, by technical point service id.
   *
   * Their discounts are the ones agreed when the sale was rung up, so campaigns are not applied
   * to them on top. A service the operator unticks and ticks again leaves this set, which is
   * right: at that point it is a new line and today's campaign is the one that applies.
   */
  private restoredServiceIds = new Set<number>();

  /** Quantity box next to each additional service, keyed by technical point service id. */
  serviceQuantities: { [techPointServiceId: number]: number } = {};
  /** Quantity box next to each catalogue result, keyed by iCash article id. */
  itemQuantities: { [saleItemId: number]: number } = {};

  // Booleans
  dataLoaded = false;
  disableActionBtn = false;
  servicesLoading = false;
  catalogLoading = false;
  promocodeLoading = false;
  /** Rejections only, cleared automatically; a successful code speaks through its chip. */
  promocodeMessage: string | null = null;
  /** Says why a free service's quantity was pulled back to one. Clears itself like the above. */
  campaignMessage: string | null = null;
  /** Which article's stock is being checked, so only that row's button shows a spinner. */
  checkingStockForSaleItemId: number | null = null;
  /** Keyed by iCash article id, so the message sits on the row it concerns. */
  itemMessages: { [saleItemId: number]: string } = {};

  // Form
  mainForm = this.formBuilder.group({
    techPointMobileAppId: [null as number | null, Validators.required],
    regNumber:            [null as string | null],
    rvsCategoryId:        [null as number | null, Validators.required],
    identNum:             [{ value: null as string | null, disabled: true }],
    promoCode:            [null as string | null],
    itemSearch:           [null as string | null],
    paymentTypeId:        [this.PAYMENT_TYPE_IN_TECH_POINT_PAY_DESK as number | null, Validators.required],
    // Optional, but exactly the seven digits printed on the receipt when it is filled in. The
    // pattern only bites once something is typed, so leaving it empty still saves.
    fiscalNumber:         [null as string | null, Validators.pattern(/^[0-9]{7}$/)],
    // When the sale happened. Filled with the current moment on a new sale and with the stored
    // one on an edit; only an operator holding CAN_EDIT_SALE_DATE can change it.
    saleDate:             [null as Date | null, Validators.required]
  });

  /**
   * The latest moment the picker will offer, and the ceiling the field is validated against.
   *
   * A field rather than a getter: owl-date-time reads it on every change detection pass, and a
   * getter returning a fresh Date each time would make the bound move under the picker while it
   * is open. It is refreshed when the picker opens instead, so a screen left standing at a
   * counter all morning still offers the current moment. The backend applies the same rule with
   * a minute of slack for the clock difference between the two machines.
   */
  maxSaleDate: Date = new Date();

  // Observables
  private itemSearchSubject = new Subject<string>();
  private subscriptions = new Subscription();

  // Timers that clear the transient rejections. Held so a later message cancels the earlier
  // one's timer rather than being wiped by it, and so ngOnDestroy can cancel them all.
  private promocodeMessageTimeout: any = null;
  private campaignMessageTimeout: any = null;
  private itemMessageTimeouts: { [saleItemId: number]: any } = {};

  constructor(
    private router:                     Router,
    private route:                      ActivatedRoute,
    private formBuilder:                FormBuilder,
    private dialog:                     MatDialog,
    private uiEvent:                    UIEventCustom,
    private translateService:           TranslateService,
    private saleService:                SaleService,
    private nomenclatureService:        NomenclatureService,
    private promotionService:           PromotionService,
    private promotionalCampaign:        PromotionalCampaign,
    private techPointService:           TechnicalPointService,
    private technicalInspectionService: TechnicalInspectionService,
    public  perms:                      PermissionsService
  ) {
    super(router);
    this.userTechPointMobileAppIds = AuthenticationService.getEmployeeTechPointsIds();
  }

  ngOnInit() {
    super.ngOnInit();

    this.subscriptions.add(
      this.itemSearchSubject.asObservable().pipe(
        debounceTime(this.SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(term => {
          const techPointMobileAppId = this.mainForm.get('techPointMobileAppId')?.value;
          if (!term || techPointMobileAppId == null) {
            this.catalogResults = [];
            this.catalogLoading = false;
            return EMPTY;
          }
          this.catalogLoading = true;
          return this.saleService.findCatalogItems(techPointMobileAppId, term, 1, this.CATALOG_PAGE_SIZE).pipe(
            catchError(err => {
              displayError(err);
              this.catalogLoading = false;
              this.uiEvent.displayUIError(this.resolveErrorMessage(err));
              return EMPTY;
            })
          );
        })
      ).subscribe(page => {
        this.catalogResults = page?.content ?? [];
        this.catalogLoading = false;
        this.catalogResults.forEach(item => {
          if (this.itemQuantities[item.saleItemId] == null) {
            this.itemQuantities[item.saleItemId] = 1;
          }
        });
      })
    );

    // The point the operator is working at drives both the services list and the iCash store,
    // so everything else waits on it being set.
    if (this.userTechPointMobileAppIds.length > 0) {
      this.mainForm.get('techPointMobileAppId')?.setValue(this.userTechPointMobileAppIds[0]);
    }

    this.loadCategories();
    this.loadPaymentTypes();
    this.loadActiveDiscountCampaigns();

    this.saleId = this.route.snapshot.queryParams['id'] == null
        ? null : Number(this.route.snapshot.queryParams['id']);
    // Carried from the list, which already knows: the sale's own payload describes the order,
    // not the documents raised against it.
    this.hasInvoice = this.route.snapshot.queryParams['hasInvoice'] === 'true';

    if (this.saleId == null) {
      // A new sale is happening now. An edit overwrites this with the date it was made on.
      this.mainForm.get('saleDate')?.setValue(new Date());
      this.onTechPointChange();
    } else {
      // Editing drives the screen from the stored sale instead of the defaults, so the point and
      // category are taken from it before the services it refers to are loaded.
      this.loadSaleForEdit(this.saleId);
    }
  }

  get isEdit(): boolean {
    return this.saleId != null;
  }

  /**
   * Whether this operator may set the sale's date themselves.
   *
   * Without it the field is readonly and its picker will not open, so the current moment is
   * stamped for them. The backend takes whatever date it is sent, so this gates the screen
   * rather than the endpoint - it keeps an ordinary counter from backdating by accident, not a
   * determined caller from doing it on purpose.
   */
  get canEditSaleDate(): boolean {
    return this.perms.hasAccess(this.perms.CAN_EDIT_SALE_DATE);
  }

  /**
   * Moves the picker's ceiling up to the present before it opens.
   *
   * The screen can stand open at a counter for hours, and a bound fixed when the component was
   * built would by then be refusing the current moment.
   */
  onSaleDatePickerOpen() {
    this.maxSaleDate = new Date();
  }

  /**
   * Loads a stored sale back into the form.
   *
   * The order matters: the technical point and the category decide which services the point
   * offers, so they are patched first and the sale's own lines are restored only once that list
   * has arrived — a service line has nothing to tick before then.
   */
  private loadSaleForEdit(saleId: number) {
    this.dataLoaded = false;

    this.subscriptions.add(
      this.saleService.findById(saleId).subscribe({
        next: sale => {
          this.editedSale = sale;

          this.mainForm.patchValue({
            techPointMobileAppId: sale.techPointMobileAppId,
            regNumber: sale.regNumber,
            rvsCategoryId: sale.rvsCategoryId,
            paymentTypeId: sale.order?.paymentType?.id ?? this.PAYMENT_TYPE_IN_TECH_POINT_PAY_DESK,
            fiscalNumber: sale.fiscalNumber ?? null,
            // The day the sale was made on, not today: an edit corrects what was sold, and the
            // sale stays where the reports already count it unless somebody deliberately moves
            // it. Parsed rather than handed to new Date(), which cannot read the dotted format
            // this DTO is annotated with and would leave the picker empty.
            saleDate: parseDtoDateTime(sale.date) ?? new Date()
          });

          if (sale.recipient != null) {
            this.recipient = sale.recipient;
            this.mainForm.get('identNum')?.setValue(sale.recipient.identNum);
          }

          this.dataLoaded = true;

          // The form is behind *ngIf="dataLoaded", so everything above was patched into controls
          // whose inputs did not exist yet. A native input picks its value up when it appears;
          // the owl-date-time directive reads its control once as it initialises and then stops
          // listening, so the date it was given before that moment never reaches the box. Setting
          // it again after the form is in the DOM is what puts it on screen.
          setTimeout(() => this.mainForm.get('saleDate')
                               ?.setValue(parseDtoDateTime(sale.date) ?? new Date()));

          // Resolves the local point id and loads the services. Both the item and the service
          // lines are restored from there: this clears the bill on its way, so restoring any
          // of them beforehand would only have them wiped.
          this.onTechPointChange();
        },
        error: err => {
          displayError(err);
          this.dataLoaded = false;
          this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
        }
      })
    );
  }

  /** Items carry their own snapshot, so they are restored without waiting for anything. */
  private restoreItemLines(sale: Sale) {
    (sale.saleItems ?? []).forEach(item => {
      this.billLines.push({
        kind: 'ITEM',
        name: item.name ?? '',
        quantity: item.quantity ?? 1,
        unitPrice: Number(item.price ?? 0),
        discount: Number(item.discountAmount ?? 0),
        saleItemId: item.saleItemId
      });
    });
  }

  /**
   * Restores the sale's service lines once the point's services are known.
   *
   * A stored line names a technical point service; it is kept only when that service is still
   * offered for the chosen category, since a line the operator cannot see is one they cannot
   * correct either.
   */
  private restoreServiceLines() {
    const sale = this.editedSale;
    if (sale == null) { return; }

    this.restoreItemLines(sale);

    (sale.saleServices ?? []).forEach(saleService => {
      const service = this.additionalServices
                          .find(candidate => candidate.id === saleService.technicalPointServiceId);
      if (service == null) { return; }

      const quantity = saleService.quantity ?? 1;
      this.serviceQuantities[service.id] = quantity;

      const storedDiscount = Number(saleService.discountAmount ?? 0);
      this.billLines.push({
        kind: 'SERVICE',
        name: (service.serviceName ?? '').toString(),
        quantity: quantity,
        unitPrice: Number(service.priceAttribute?.amountDds ?? service.amountDds ?? 0),
        discount: storedDiscount,
        // The stored figure stands as the operator's own: it is what the sale was agreed at,
        // whatever produced it, and the max rule must not let a recalculation drop below it.
        typedDiscount: storedDiscount,
        techPointServiceId: service.id
      });
      this.restoredServiceIds.add(service.id);

      if (saleService.promoCode) {
        this.restorePromocode(saleService.promoCode, service);
      }
    });

    // Restoring is a one-off: a later category change rebuilds the lines from the screen.
    this.editedSale = null;
  }

  /**
   * Puts back a promo code the sale was saved with.
   *
   * The code is re-validated rather than taken on trust, so its chip carries the real discount
   * kind and value. Without them any later change to the bill would recalculate the line to a
   * zero discount and silently undo the code. A code that no longer validates is dropped and
   * the line keeps the discount already stored against it.
   */
  private restorePromocode(code: string, service: TechPointService) {
    const category = this.rvsCategories.find(c => c.id === this.mainForm.get('rvsCategoryId')?.value);
    if (category == null) { return; }

    this.subscriptions.add(
      this.promotionService.validatePromocode({
        promoCode: code,
        rvsCategoryCode: category.code,
        technicalPointMobileAppId: this.mainForm.get('techPointMobileAppId')?.value ?? undefined
      }).subscribe({
        next: result => {
          if (result == null || result.promocodeId == null) { return; }
          this.appliedPromocodes.push({
            code: code,
            serviceName: (service.serviceName ?? '').toString(),
            techPointServiceId: service.id,
            promocodeId: result.promocodeId,
            discountKind: result.discountType ?? this.DISCOUNT_PERCENTAGE,
            discountValue: result.discountValue ?? 0
          });
        },
        // Silent: the sale is readable and correctable without the chip, and an error toast
        // while merely opening a screen would be noise.
        error: () => {}
      })
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.unsubscribe();

    // Pending timers would otherwise fire against a destroyed component.
    if (this.promocodeMessageTimeout) { clearTimeout(this.promocodeMessageTimeout); }
    if (this.campaignMessageTimeout) { clearTimeout(this.campaignMessageTimeout); }
    Object.values(this.itemMessageTimeouts).forEach(timeout => clearTimeout(timeout));
  }

  get f() { return this.mainForm.controls; }

  /**
   * Whether the fiscal number reads as a complete receipt number, for the green confirmation on
   * the field.
   *
   * The control is optional, so "no errors" is not the same as "correct": an empty box passes the
   * pattern and must stay neutral rather than claim to be filled in correctly. Only the seven
   * digits actually printed on the receipt earn the tick.
   */
  get isFiscalNumberComplete(): boolean {
    const value = this.mainForm.get('fiscalNumber')?.value;
    return typeof value === 'string' && /^[0-9]{7}$/.test(value.trim());
  }

  get hasSingleTechPoint(): boolean {
    return this.userTechPointMobileAppIds.length <= 1;
  }

  private loadCategories() {
    this.subscriptions.add(
      this.nomenclatureService.getRvsCategories().subscribe({
        next: categories => {
          this.rvsCategories = categories;
          this.dataLoaded = true;
        },
        error: err => {
          displayError(err);
          this.dataLoaded = false;
          this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
        }
      })
    );
  }

  /**
   * The payment types a counter sale can take, in the order the nomenclature returns them.
   *
   * A failure here is not fatal to the screen: the dropdown stays empty and the required
   * validator stops the sale being saved without one, rather than blocking the whole form the
   * way a missing category list would.
   */
  private loadPaymentTypes() {
    this.subscriptions.add(
      this.nomenclatureService.getPaymentTypes().subscribe({
        next: paymentTypes => {
          this.paymentTypes = paymentTypes.filter(
              paymentType => this.SALE_PAYMENT_TYPE_IDS.includes(paymentType.id));
        },
        error: err => {
          displayError(err);
          this.uiEvent.displayUIError(this.resolveErrorMessage(err));
        }
      })
    );
  }

  /**
   * Resolves the local technical point id — the services endpoint is keyed by it, while the
   * rest of the screen works with the mobile app id from the token.
   */
  onTechPointChange() {
    const techPointMobileAppId = this.mainForm.get('techPointMobileAppId')?.value;
    this.clearBill();
    this.additionalServices = [];
    this.catalogResults = [];

    if (techPointMobileAppId == null) { return; }

    this.subscriptions.add(
      this.techPointService.getTechPointIdByTechPointMobileAppId(techPointMobileAppId).subscribe({
        next: techPointId => {
          this.technicalPointId = techPointId;
          this.loadAdditionalServices();
        },
        error: err => {
          displayError(err);
          this.uiEvent.displayUIError(this.resolveErrorMessage(err));
        }
      })
    );
  }

  /** The category decides which services the point offers, so changing it reloads them. */
  onCategoryChange() {
    this.removeLinesOfKind('SERVICE');
    this.clearPromocodes();
    this.loadAdditionalServices();
  }

  private loadAdditionalServices() {
    const rvsCategoryId = this.mainForm.get('rvsCategoryId')?.value;
    if (rvsCategoryId == null || this.technicalPointId == null) {
      this.additionalServices = [];
      return;
    }

    this.servicesLoading = true;
    this.subscriptions.add(
      this.technicalInspectionService
          .getTechPointServicesByType(this.ADDITIONAL_SERVICE_TYPE, rvsCategoryId, this.technicalPointId)
          .subscribe({
            next: services => {
              this.additionalServices = services ?? [];
              this.servicesLoading = false;
              // Only ever non-null right after an edit loaded a sale: its service lines could
              // not be rebuilt until the point's services were known.
              this.restoreServiceLines();
            },
            error: err => {
              displayError(err);
              this.servicesLoading = false;
              this.additionalServices = [];
              this.uiEvent.displayUIError(this.resolveErrorMessage(err));
            }
          })
    );
  }

  /**
   * Fills the category in from the registration number.
   *
   * A plate that resolves to nothing is a normal outcome at a counter, so whatever the operator
   * already chose is left in place and no error is shown.
   */
  onRegNumberBlur() {
    const regNumber = this.mainForm.get('regNumber')?.value;
    if (!regNumber || !regNumber.trim()) { return; }

    this.subscriptions.add(
      this.saleService.findCategoryByRegNumber(regNumber.trim()).subscribe({
        next: category => {
          if (category == null || category.id == null) { return; }
          if (this.mainForm.get('rvsCategoryId')?.value === category.id) { return; }
          this.mainForm.get('rvsCategoryId')?.setValue(category.id);
          this.onCategoryChange();
        },
        error: err => displayError(err)
      })
    );
  }

  /** Opens the register of legal persons; it searches the registry agency by ЕИК itself. */
  openSubjectModal() {
    if (this.dialog.openDialogs.length !== 0) { return; }

    const ref = this.dialog.open(RegisterOfLegalPersonsModalComponent, modalMinWidth);
    this.subscriptions.add(
      ref.componentInstance.submitSubject.subscribe((subject: SubjectVersion) => {
        if (subject == null) { return; }
        this.recipient = subject;
        this.mainForm.get('identNum')?.setValue(subject.identNum);
      })
    );
  }

  clearRecipient() {
    this.recipient = null;
    this.mainForm.get('identNum')?.setValue(null);
  }

  // --- Additional services -------------------------------------------------

  isServiceSelected(service: TechPointService): boolean {
    return this.billLines.some(line => line.kind === 'SERVICE' && line.techPointServiceId === service.id);
  }

  toggleService(service: TechPointService, checked: boolean) {
    if (!checked) {
      this.billLines = this.billLines.filter(
        line => !(line.kind === 'SERVICE' && line.techPointServiceId === service.id));
      // A service taken off the bill is no longer one of the sale's stored lines: should it be
      // put back, it is a new line and today's campaign applies to it.
      this.restoredServiceIds.delete(service.id);
      // Drops any code bound to the service that just left the bill. Removing a trigger service
      // also ends the free-service campaigns it was holding open, which this recalculation
      // notices because the bill it reads no longer contains the trigger.
      this.recalculatePromocodeDiscounts();
      return;
    }

    if (this.serviceQuantities[service.id] == null) {
      this.serviceQuantities[service.id] = 1;
    }

    this.billLines.push({
      kind: 'SERVICE',
      name: (service.serviceName ?? '').toString(),
      quantity: this.serviceQuantities[service.id],
      unitPrice: service.priceAttribute?.amountDds ?? service.amountDds ?? 0,
      discount: 0,
      techPointServiceId: service.id
    });

    this.recalculatePromocodeDiscounts();
  }

  onServiceQuantityChange(service: TechPointService) {
    let quantity = this.normalizeQuantity(this.serviceQuantities[service.id]);

    const line = this.billLines.find(l => l.kind === 'SERVICE' && l.techPointServiceId === service.id);

    // The campaign gives away one of this service, so the box is held at one rather than letting
    // a line of three be taken at a hundred percent off.
    if (line?.campaign?.isFree && quantity > 1) {
      quantity = 1;
      this.showCampaignMessage(
          this.translateService.instant('sales.campaign.freeQuantityReset', { service: line.name }));
    }

    this.serviceQuantities[service.id] = quantity;

    if (line) {
      line.quantity = quantity;
      // Every figure on the line is a function of its total, so they are all worked out again
      // rather than the stored discount merely being capped.
      this.recalculatePromocodeDiscounts();
    }
  }

  // --- Goods ---------------------------------------------------------------

  onItemSearchChange() {
    const term = this.mainForm.get('itemSearch')?.value;
    this.itemSearchSubject.next((term ?? '').trim());
  }

  /**
   * Adds an article to the bill, but only once iCash confirms the stock is there.
   *
   * The qty shown in the search list comes from iCash's last sync and can be out of date, so the
   * stock is read live here — at the one moment it matters — rather than trusted from the list.
   * The check is against the running total for the article, not just the amount being added, so
   * repeated small additions cannot walk past the stock a unit at a time.
   */
  addCatalogItem(item: IcashCatalogItem) {
    const techPointMobileAppId = this.mainForm.get('techPointMobileAppId')?.value;
    if (techPointMobileAppId == null) { return; }

    const quantity = this.normalizeQuantity(this.itemQuantities[item.saleItemId]);
    const existing = this.billLines.find(line => line.kind === 'ITEM' && line.saleItemId === item.saleItemId);
    const requested = (existing?.quantity ?? 0) + quantity;

    this.clearItemMessage(item.saleItemId);
    this.checkingStockForSaleItemId = item.saleItemId;

    this.subscriptions.add(
      this.saleService.findLiveCatalogItem(techPointMobileAppId, item.saleItemId).subscribe({
        next: live => {
          this.checkingStockForSaleItemId = null;

          // iCash sends qty null when it cannot determine the stock, which is not the same as
          // zero — treat it as unknown and let the add through rather than blocking on a figure
          // that was never reported.
          const available = live?.qty;
          if (available != null && available < requested) {
            this.showItemMessage(item.saleItemId,
                this.translateService.instant('sales.goods.notEnoughQuantity', { available: available }));
            return;
          }

          this.commitCatalogItem(item, live, quantity, existing);
        },
        error: err => {
          displayError(err);
          this.checkingStockForSaleItemId = null;
          // No confirmation of stock means no add: the whole point of the check is to keep the
          // counter from selling what is not there.
          this.showItemMessage(item.saleItemId, this.resolveErrorMessage(err));
        }
      })
    );
  }

  /** Puts the article on the bill once its stock has been confirmed. */
  private commitCatalogItem(item: IcashCatalogItem, live: IcashCatalogItem | null,
                            quantity: number, existing: BillLine | undefined) {
    // The same article added twice is one line with a larger quantity, not two lines — that is
    // how it is rung up and how the chip above the bill reads.
    if (existing) {
      existing.quantity += quantity;
    } else {
      this.billLines.push({
        kind: 'ITEM',
        name: item.name,
        quantity: quantity,
        // The live reading is the fresher price, so it wins over the one the search returned.
        unitPrice: live?.sellPrice ?? item.sellPrice ?? 0,
        discount: 0,
        saleItemId: item.saleItemId
      });
    }

    this.mainForm.get('itemSearch')?.setValue(null);
    this.catalogResults = [];
    this.itemQuantities[item.saleItemId] = 1;
  }

  /** A rejection on one picker row, cleared on the same timer as the promo code's. */
  private showItemMessage(saleItemId: number, message: string) {
    this.clearItemMessage(saleItemId);
    this.itemMessages[saleItemId] = message;

    this.itemMessageTimeouts[saleItemId] = setTimeout(() => {
      delete this.itemMessages[saleItemId];
      delete this.itemMessageTimeouts[saleItemId];
    }, this.MESSAGE_TIMEOUT_MS);
  }

  private clearItemMessage(saleItemId: number) {
    if (this.itemMessageTimeouts[saleItemId]) {
      clearTimeout(this.itemMessageTimeouts[saleItemId]);
      delete this.itemMessageTimeouts[saleItemId];
    }
    delete this.itemMessages[saleItemId];
  }

  get itemLines(): BillLine[] {
    return this.billLines.filter(line => line.kind === 'ITEM');
  }

  removeLine(line: BillLine) {
    this.billLines = this.billLines.filter(l => l !== line);
  }

  // --- Bill ----------------------------------------------------------------

  lineTotal(line: BillLine): number {
    return this.round(this.lineGross(line) - (line.discount ?? 0));
  }

  lineGross(line: BillLine): number {
    return this.round(line.unitPrice * line.quantity);
  }

  get grandTotal(): number {
    return this.round(this.billLines.reduce((sum, line) => sum + this.lineTotal(line), 0));
  }

  /**
   * The campaigns in force on the bill, one chip each.
   *
   * Only those actually taking money off are listed: a campaign whose discount lost out to a
   * larger promo code is not what the customer is getting, and naming it would explain the price
   * wrongly.
   */
  get activeCampaignChips(): AppliedCampaign[] {
    return this.billLines
        .filter(line => line.kind === 'SERVICE' && line.campaign != null
            && this.campaignDiscountFor(line, this.lineGross(line)) >= line.discount
            && line.discount > 0)
        .map(line => line.campaign!);
  }

  /**
   * A discount larger than the line is worth would make the sale pay the customer.
   *
   * What the operator typed is remembered separately before anything else happens to it: on a
   * service line the figure below is only a candidate, and a code or a campaign worth more will
   * replace it. Without the record their entry would be lost the moment either recalculated.
   */
  capLineDiscount(line: BillLine) {
    const gross = this.lineGross(line);
    if (line.discount == null || line.discount < 0) {
      line.discount = 0;
    } else if (line.discount > gross) {
      line.discount = gross;
    }
    line.discount = this.round(line.discount);

    if (line.kind === 'SERVICE') {
      line.typedDiscount = line.discount;
      this.recalculatePromocodeDiscounts();
    }
  }

  // --- Promo code ----------------------------------------------------------

  /**
   * Validates the typed code and binds it to the one selected service it discounts.
   *
   * Several codes can be in force at once, one per service — the same rule the technical
   * inspection screen applies. A code is rejected when it names no service currently on the
   * bill, or when the service it names already carries a code.
   */
  applyPromocode() {
    const promoCode = this.mainForm.get('promoCode')?.value;
    const rvsCategoryId = this.mainForm.get('rvsCategoryId')?.value;
    const techPointMobileAppId = this.mainForm.get('techPointMobileAppId')?.value;

    this.clearPromocodeError();

    if (!promoCode || !promoCode.trim()) { return; }

    const code = promoCode.trim();

    const category = this.rvsCategories.find(c => c.id === rvsCategoryId);
    if (category == null) {
      this.showPromocodeError(this.translateService.instant('sales.promoCode.categoryRequired'));
      return;
    }

    if (this.appliedPromocodes.some(applied => applied.code.toUpperCase() === code.toUpperCase())) {
      this.showPromocodeError(this.translateService.instant('sales.promoCode.alreadyApplied'));
      return;
    }

    this.promocodeLoading = true;
    this.subscriptions.add(
      this.promotionService.validatePromocode({
        promoCode: code,
        rvsCategoryCode: category.code,
        technicalPointMobileAppId: techPointMobileAppId ?? undefined
      }).subscribe({
        next: result => {
          this.promocodeLoading = false;
          this.bindPromocode(code, result);
        },
        error: err => {
          displayError(err);
          this.promocodeLoading = false;
          this.showPromocodeError(this.resolveErrorMessage(err));
        }
      })
    );
  }

  private bindPromocode(code: string, result: CustomerPromocodeMobileDTO) {
    if (result == null || result.promocodeId == null) {
      this.showPromocodeError(this.translateService.instant('sales.promoCode.invalid'));
      return;
    }

    // The code carries the services it is good for; it discounts whichever of them is on the
    // bill. A code naming nothing in particular is taken to apply to any one service.
    const namedServices = (result.services ?? []).map(service => service.name).filter(name => !!name);
    const serviceLines = this.billLines.filter(line => line.kind === 'SERVICE');
    const candidates = namedServices.length === 0
        ? serviceLines
        : serviceLines.filter(line => namedServices.includes(line.name));

    if (candidates.length === 0) {
      this.showPromocodeError(this.translateService.instant('sales.promoCode.serviceNotSelected'));
      return;
    }

    const target = candidates.find(line => !this.isServiceDiscounted(line.techPointServiceId!));
    if (target == null) {
      this.showPromocodeError(this.translateService.instant('sales.promoCode.discountAlreadyExists'));
      return;
    }

    this.appliedPromocodes.push({
      code: code,
      serviceName: target.name,
      techPointServiceId: target.techPointServiceId!,
      promocodeId: result.promocodeId,
      discountKind: result.discountType ?? this.DISCOUNT_PERCENTAGE,
      discountValue: result.discountValue ?? 0
    });

    this.recalculatePromocodeDiscounts();

    // The field is cleared so the next code can be typed straight in; what was applied is
    // readable from the chips below it.
    this.mainForm.get('promoCode')?.setValue(null);
    // Nothing is said on success: the chip and the discounted bill line already show it worked.
    this.clearPromocodeError();
  }

  removePromocode(applied: AppliedPromocode) {
    this.appliedPromocodes = this.appliedPromocodes.filter(p => p !== applied);
    this.recalculatePromocodeDiscounts();
    this.clearPromocodeError();
  }

  clearPromocodes() {
    this.appliedPromocodes = [];
    this.recalculatePromocodeDiscounts();
    this.clearPromocodeError();
  }

  private isServiceDiscounted(techPointServiceId: number): boolean {
    return this.appliedPromocodes.some(applied => applied.techPointServiceId === techPointServiceId);
  }

  /** Whether a campaign is giving this service away, so its quantity box stays at one. */
  isServiceFreeByCampaign(techPointServiceId: number): boolean {
    return this.billLines.some(line => line.kind === 'SERVICE'
        && line.techPointServiceId === techPointServiceId
        && line.campaign?.isFree === true);
  }

  /**
   * The campaigns running right now. A failure is reported but not fatal: a sale can still be
   * rung up at full price, which is better than a counter that cannot sell at all.
   */
  private loadActiveDiscountCampaigns() {
    this.subscriptions.add(
      this.promotionalCampaign.getActiveDiscountCampaigns().subscribe({
        next: res => {
          this.activeDiscountCampaigns = res.activeDiscountCampaigns ?? [];
          // Services may already be on the bill by the time this arrives - the call races the
          // sale being restored - so the lines are priced again now that the campaigns are known.
          this.recalculatePromocodeDiscounts();
        },
        error: err => displayError(err)
      })
    );
  }

  /**
   * Recomputes every service line's discount from the codes and campaigns currently in force.
   *
   * Recalculating all of them, rather than adjusting one, keeps the lines correct after a
   * quantity changes or a code is removed. Goods are never touched: promotions are configured
   * against services, and an item line keeps whatever the operator typed.
   *
   * Three figures can apply to one line and the largest of them wins, so a campaign is a floor
   * rather than an alternative: applying a weaker promo code, or typing a smaller figure, cannot
   * quietly take back a discount the customer was already entitled to. A free-service campaign
   * is the extreme of the same rule - its discount is the whole line.
   */
  private recalculatePromocodeDiscounts() {
    // A code whose service has since left the bill is dropped with it.
    this.appliedPromocodes = this.appliedPromocodes.filter(applied =>
        this.billLines.some(line => line.kind === 'SERVICE'
            && line.techPointServiceId === applied.techPointServiceId));

    this.refreshCampaignsOnLines();

    this.billLines
        .filter(line => line.kind === 'SERVICE')
        .forEach(line => {
          const gross = this.lineGross(line);
          const candidates = [
            this.promocodeDiscountFor(line, gross),
            this.campaignDiscountFor(line, gross),
            line.typedDiscount ?? 0
          ];
          line.discount = Math.min(this.round(Math.max(...candidates)), gross);
        });
  }

  /** What the promo code bound to this line is worth, or 0 when none is. */
  private promocodeDiscountFor(line: BillLine, gross: number): number {
    const applied = this.appliedPromocodes.find(
        p => p.techPointServiceId === line.techPointServiceId);
    if (applied == null) {
      return 0;
    }
    return applied.discountKind === this.DISCOUNT_PERCENTAGE
        ? this.round(gross * (applied.discountValue / 100))
        : Math.min(this.round(applied.discountValue), gross);
  }

  /** What the campaign on this line is worth, or 0 when none applies. */
  private campaignDiscountFor(line: BillLine, gross: number): number {
    if (line.campaign == null) {
      return 0;
    }
    if (line.campaign.isFree) {
      return gross;
    }
    const discount = this.promotionalCampaign.getTriggerServiceDiscount(
        line.campaign.serviceName, this.activeDiscountCampaigns);
    if (discount == null) {
      return 0;
    }
    return discount.discountKind === this.DISCOUNT_PERCENTAGE
        ? this.round(gross * (discount.discountValue / 100))
        : Math.min(this.round(discount.discountValue), gross);
  }

  /**
   * Marks each service line with the campaign that applies to it, if any.
   *
   * Lines restored from a stored sale are left alone: their discount is the one that was agreed
   * when the sale was rung up, and re-pricing them against today's campaigns would change the
   * total of a sale that may already have been invoiced.
   *
   * A free service is pulled back to a quantity of one. The campaign gives away one of it, and a
   * line of three at a hundred percent off would be giving away three.
   */
  private refreshCampaignsOnLines() {
    if (this.activeDiscountCampaigns.length === 0) {
      return;
    }

    const serviceLines = this.billLines.filter(line => line.kind === 'SERVICE');
    // isEligibleForFreeCampaign matches on serviceName, but it is given the point's own service
    // objects rather than stand-ins built from the lines: the campaign check is shared with the
    // inspection screen, and handing it a half-built object would break the moment it read any
    // other field. A line whose service is no longer offered simply has no campaign.
    const servicesOnBill = serviceLines
        .map(line => this.additionalServices.find(s => s.id === line.techPointServiceId))
        .filter((s): s is TechPointService => s != null);

    serviceLines.forEach(line => {
      if (this.isRestoredLine(line)) {
        return;
      }

      const service = this.additionalServices.find(s => s.id === line.techPointServiceId);
      if (service == null) {
        return;
      }

      if (this.promotionalCampaign.isEligibleForFreeCampaign(
              service, servicesOnBill, this.activeDiscountCampaigns)) {
        this.applyFreeCampaignTo(line);
        return;
      }

      const discount = this.promotionalCampaign.getTriggerServiceDiscount(
          line.name, this.activeDiscountCampaigns);
      line.campaign = discount == null ? undefined : {
        serviceName: line.name,
        techPointServiceId: line.techPointServiceId!,
        discountText: discount.discountText,
        isFree: false
      };
    });
  }

  /** Marks a line free and holds it to one unit, saying so the first time it happens. */
  private applyFreeCampaignTo(line: BillLine) {
    const wasAlreadyFree = line.campaign?.isFree === true;

    line.campaign = {
      serviceName: line.name,
      techPointServiceId: line.techPointServiceId!,
      discountText: this.translateService.instant('sales.campaign.free'),
      isFree: true
    };

    if (line.quantity > 1) {
      line.quantity = 1;
      if (line.techPointServiceId != null) {
        this.serviceQuantities[line.techPointServiceId] = 1;
      }
      if (!wasAlreadyFree) {
        this.showCampaignMessage(
            this.translateService.instant('sales.campaign.freeQuantityReset', { service: line.name }));
      }
    }
  }

  /**
   * Whether this line came from a stored sale rather than being added in this session.
   *
   * A restored line carries a discount that was decided when the sale was made; this screen must
   * not second-guess it. Lines added since have no stored counterpart and are priced live.
   */
  private isRestoredLine(line: BillLine): boolean {
    return this.restoredServiceIds.has(line.techPointServiceId!);
  }

  /** Shows the campaign notice for a few seconds, the way a promo code rejection is shown. */
  private showCampaignMessage(message: string) {
    if (this.campaignMessageTimeout) {
      clearTimeout(this.campaignMessageTimeout);
    }
    this.campaignMessage = message;
    this.campaignMessageTimeout = setTimeout(() => {
      this.campaignMessage = null;
      this.campaignMessageTimeout = null;
    }, this.MESSAGE_TIMEOUT_MS);
  }

  /**
   * Shows a rejection under the promo code field for a few seconds.
   *
   * Only failures are reported here. A code that worked is already evident from the chip that
   * appears and the discount on the bill, so repeating it in prose would be noise.
   */
  private showPromocodeError(message: string | null) {
    if (this.promocodeMessageTimeout) {
      clearTimeout(this.promocodeMessageTimeout);
      this.promocodeMessageTimeout = null;
    }

    this.promocodeMessage = message;

    if (message == null) { return; }

    this.promocodeMessageTimeout = setTimeout(() => {
      this.promocodeMessage = null;
      this.promocodeMessageTimeout = null;
    }, this.MESSAGE_TIMEOUT_MS);
  }

  private clearPromocodeError() {
    this.showPromocodeError(null);
  }

  // --- Save ----------------------------------------------------------------

  save() {
    this.persist(() => {
      this.uiEvent.displayUISuccess();

      if (this.isEdit) {
        // An edit is a correction of one sale, so it ends by going back to the list it came
        // from. Creating is the other way round: a counter serves one walk-in after another,
        // so the form is cleared and left ready rather than navigating away.
        this.router.navigate(['/list-sales']);
        return;
      }

      this.resetForNextSale();
    });
  }

  /**
   * Saves the sale, then opens the invoice screen for it.
   *
   * The invoice is raised against a stored sale rather than against the form, so the sale must
   * exist first — the same order the technical inspection screen follows. The draft itself is
   * built by the backend from the sale's order, so its lines already carry the discounted
   * totals, and its recipient is the sale's subject version when there was one.
   */
  issueInvoice() {
    this.persist(saleId => {
      this.uiEvent.displayUISuccess();
      this.router.navigate(['/add-edit-accounting-document'], { queryParams: { saleId: saleId } });
    });
  }

  /**
   * Asks before voiding the sale. There is no way back from the screen once it is done - the
   * sale leaves the list - and the button sits beside the two that save, where a misclick costs
   * the operator a sale they have just rung up.
   */
  confirmAnnul() {
    if (this.dialog.openDialogs.length !== 0) { return; }

    const ref = this.dialog.open(this.annulConfirmDialog, { width: '600px' });
    this.subscriptions.add(
      ref.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed === true) {
          this.annul();
        }
      })
    );
  }

  /**
   * Voids the sale and returns to the list.
   *
   * Nothing on the form is sent: the backend copies what is stored rather than taking the
   * screen's word for it, so an unsaved edit sitting in the form cannot be smuggled in by
   * annulling. Equally, an edit the operator has typed but not saved is simply discarded.
   */
  private annul() {
    this.disableActionBtn = true;

    this.subscriptions.add(
      this.saleService.annul(this.saleId!).subscribe({
        next: () => {
          this.disableActionBtn = false;
          this.uiEvent.displayUISuccess(this.translateService.instant('sales.annul.success'));
          this.router.navigate(['/list-sales']);
        },
        error: err => {
          displayError(err);
          this.disableActionBtn = false;
          this.uiEvent.displayUIError(this.resolveErrorMessage(err));
        }
      })
    );
  }

  /**
   * Validates, builds the payload and saves. Both buttons go through here, and both are
   * disabled while it runs, so a sale cannot be created twice over.
   */
  private persist(onSaved: (saleId: number) => void) {
    if (!this.mainForm.valid) {
      this.uiEvent.displayUIError();
      this.showValidationsSubject.next(true);
      return;
    }

    if (this.billLines.length === 0) {
      this.uiEvent.displayUIError(this.translateService.instant('sales.errors.empty'));
      return;
    }

    // Each discounted line carries its own code, since a sale can have several at once.
    const saleServices: SaleServiceModel[] = this.billLines
        .filter(line => line.kind === 'SERVICE')
        .map(line => ({
          technicalPointServiceId: line.techPointServiceId!,
          quantity: line.quantity,
          discountAmount: line.discount,
          promoCode: this.appliedPromocodes
                         .find(p => p.techPointServiceId === line.techPointServiceId)?.code ?? null
        }));

    const saleItems: SaleItem[] = this.billLines
        .filter(line => line.kind === 'ITEM')
        .map(line => ({
          saleItemId: line.saleItemId!,
          quantity: line.quantity,
          discountAmount: line.discount
        }));

    const sale: Sale = {
      regNumber: this.mainForm.get('regNumber')?.value,
      rvsCategoryId: this.mainForm.get('rvsCategoryId')?.value!,
      recipientId: this.recipient?.id ?? null,
      techPointMobileAppId: this.mainForm.get('techPointMobileAppId')?.value!,
      paymentTypeId: this.mainForm.get('paymentTypeId')?.value!,
      // ?? rather than ||, which would turn a legitimate "0000000" into null.
      fiscalNumber: this.mainForm.get('fiscalNumber')?.value ?? null,
      // Sent in the format the backend parses, the same way the promotion screen sends its
      // dates. Omitted when empty so the backend falls back to its own clock rather than
      // storing nothing.
      date: this.saleDateForPayload(),
      saleServices: saleServices,
      saleItems: saleItems
    };

    this.disableActionBtn = true;

    // Editing writes a new version rather than altering the stored row, so the id that comes
    // back names the new version and the old one becomes history.
    const saved$ = this.isEdit
        ? this.saleService.update(this.saleId!, sale)
        : this.saleService.save(sale);

    this.subscriptions.add(
      saved$.subscribe({
        next: saleId => {
          this.disableActionBtn = false;
          onSaved(saleId);
        },
        error: err => {
          displayError(err);
          this.disableActionBtn = false;
          this.uiEvent.displayUIError(this.resolveErrorMessage(err));
        }
      })
    );
  }

  private resetForNextSale() {
    const techPointMobileAppId = this.mainForm.get('techPointMobileAppId')?.value;

    this.mainForm.reset();
    this.mainForm.get('techPointMobileAppId')?.setValue(techPointMobileAppId);
    // reset() empties the control, so the next walk-in would start with no payment type at
    // all rather than with the one the counter uses most.
    this.mainForm.get('paymentTypeId')?.setValue(this.PAYMENT_TYPE_IN_TECH_POINT_PAY_DESK);
    // Likewise the date, which is required: the next sale is happening now, and left empty the
    // form would never validate again.
    this.mainForm.get('saleDate')?.setValue(new Date());

    this.clearBill();
    this.clearPromocodes();
    this.clearRecipient();
    this.catalogResults = [];
    this.serviceQuantities = {};
    this.itemQuantities = {};
    this.showValidationsSubject.next(false);
  }

  private clearBill() {
    this.billLines = [];
  }

  private removeLinesOfKind(kind: 'SERVICE' | 'ITEM') {
    this.billLines = this.billLines.filter(line => line.kind !== kind);
  }

  private normalizeQuantity(quantity: number | null | undefined): number {
    const parsed = Number(quantity);
    return !parsed || parsed < 1 ? 1 : Math.floor(parsed);
  }

  private round(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  /**
   * Prefers the backend's own message for a known error code, falling back to the generic one.
   * instant() echoes the key back when there is no translation for it.
   */
  /**
   * The sale's date in the format the backend parses, or undefined when there is none.
   *
   * Two things make this its own method rather than an inline expression. A Date left to
   * serialise itself goes over the wire as UTC, which would move a sale made at 00:30 back into
   * the previous day; and SaleDTO.date is annotated with DateTimeUtil.DATETIME_FORMAT, so
   * Jackson parses it by "dd.MM.yyyy HH:mm:ss" and refuses anything else with a 400.
   */
  private saleDateForPayload(): string | undefined {
    const value = this.mainForm.get('saleDate')?.value;
    return value == null ? undefined : formatDateTimeForDto(value);
  }

  private resolveErrorMessage(err: any): string {
    const apiCode = err?.error?.code;
    const i18nKey = apiCode ? `errorCodes.${apiCode}` : null;
    const translated = i18nKey ? this.translateService.instant(i18nKey) : null;
    if (translated && translated !== i18nKey) { return translated; }
    return this.translateService.instant('messages.errorLoadingData');
  }
}
